/**
 * Sales Attribution dialog — Super Admin only.
 *
 * Lets a Super Admin attribute a Sales Person and/or a Sales Manager to a
 * customer. Sales users then see ONLY the active orders of the customers they
 * are attributed to. Uses PUT /masters/customers/{id}/sales-attribution.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';

const NONE = '__none__';

export function SalesAttributionDialog({ open, onOpenChange, companyId, customer, onSaved }) {
  const [salesUsers, setSalesUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [spId, setSpId] = useState(NONE);
  const [smId, setSmId] = useState(NONE);

  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get('/users/', { params: { company_id: companyId } });
        if (!cancelled) {
          setSalesUsers(
            (Array.isArray(data) ? data : []).filter(
              (u) => (u.role === 'sales_person' || u.role === 'sales_manager') && !u.disabled,
            ),
          );
        }
      } catch (e) {
        if (!cancelled) toast.error(extractError(e, 'Failed to load sales users'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, companyId]);

  useEffect(() => {
    if (!open) return;
    setSpId(customer?.sales_person_id || NONE);
    setSmId(customer?.sales_manager_id || NONE);
  }, [open, customer]);

  const salesPersons = useMemo(() => salesUsers.filter((u) => u.role === 'sales_person'), [salesUsers]);
  const salesManagers = useMemo(() => salesUsers.filter((u) => u.role === 'sales_manager'), [salesUsers]);

  const save = async () => {
    if (!customer) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/masters/customers/${customer.id}/sales-attribution`, {
        sales_person_id: spId === NONE ? '' : spId,
        sales_manager_id: smId === NONE ? '' : smId,
      });
      toast.success(`Sales attribution updated for "${customer.name}"`);
      onOpenChange(false);
      if (onSaved) await onSaved(data);
    } catch (e) {
      toast.error(extractError(e, 'Failed to update sales attribution'));
    } finally {
      setSaving(false);
    }
  };

  const renderLabel = (u) => `${u.name || u.email}${u.name ? ` · ${u.email}` : ''}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sales-attribution-dialog">
        <DialogHeader>
          <DialogTitle>Sales Attribution</DialogTitle>
          <DialogDescription>
            {customer
              ? `Assign a Sales Person and/or Sales Manager to "${customer.name}". They will see only this customer's active orders (read-only).`
              : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <Label>Sales Person</Label>
            <Select value={spId} onValueChange={setSpId} disabled={loading}>
              <SelectTrigger className="mt-1" data-testid="sales-attribution-person-select">
                <SelectValue placeholder={loading ? 'Loading…' : 'Select a sales person'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE} data-testid="sales-attribution-person-none">— None —</SelectItem>
                {salesPersons.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`sales-attribution-person-${u.id}`}>
                    {renderLabel(u)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!loading && salesPersons.length === 0 && (
              <div className="text-[11px] text-slate-500 mt-1">
                No Sales Person users yet. Create one under Admin → Users.
              </div>
            )}
          </div>

          <div>
            <Label>Sales Manager</Label>
            <Select value={smId} onValueChange={setSmId} disabled={loading}>
              <SelectTrigger className="mt-1" data-testid="sales-attribution-manager-select">
                <SelectValue placeholder={loading ? 'Loading…' : 'Select a sales manager'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE} data-testid="sales-attribution-manager-none">— None —</SelectItem>
                {salesManagers.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`sales-attribution-manager-${u.id}`}>
                    {renderLabel(u)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!loading && salesManagers.length === 0 && (
              <div className="text-[11px] text-slate-500 mt-1">
                No Sales Manager users yet. Create one under Admin → Users.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || loading}
            className="luxury-button"
            data-testid="sales-attribution-save-button"
          >
            {saving ? 'Saving…' : 'Save Attribution'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SalesAttributionDialog;
